import { DrawApi, FileApi } from '@apply/api';
import DataGrid, { Column, Summary, TotalItem } from 'devextreme-react/data-grid';
import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IDrawReceipt } from '@apply/api/src/api/DrawApi';
import ReceiptsModalV2 from '../modalsv2/ReceiptsModalV2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-regular-svg-icons';
import { faDownload, faFileInvoiceDollar, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { DrawBudgetCategory } from '../../functions';
import CustomStore from 'devextreme/data/custom_store';
import { Button, ButtonTheme, GenericPopup, IconButton, IconButtonSize, IconButtonTheme } from '@apply/components';

interface IReceiptModalV2Props {
    categories: DrawBudgetCategory[];
    applicationInsights: ApplicationInsights;
    isOpen: boolean;
    close: () => void;
}

const ViewReceiptsModalV2: React.FC<IReceiptModalV2Props> = ({ categories, applicationInsights, isOpen, close }) => {
    const drawApi = useMemo(() => new DrawApi.DrawApi(applicationInsights), [applicationInsights]);
    const fileApi = useMemo(() => new FileApi.FileApi(applicationInsights), [applicationInsights]);
    const dataGridRef = useMemo(() => React.createRef<DataGrid>(), []);

    const location = useLocation();
    const drawId = location.pathname.split('/')[2];

    const [openAddEditReceiptsModal, set_openAddEditReceiptsModal] = useState<boolean>(false);
    const [activeReceipt, set_activeReceipt] = useState<IDrawReceipt | undefined>(undefined);

    const customStore = useMemo(
        () =>
            new CustomStore({
                key: 'drawReceiptId',
                load: async () => {
                    const response = await drawApi.getDrawReceipts(drawId);
                    if (response.statusCode !== 200) throw Error('Could not load table data');

                    return response.result;
                },
                insert: (receipt: IDrawReceipt) => {
                    return drawApi.addOrUpdateDrawReceipt(receipt);
                },
                onInserted: () => {
                    dataGridRef?.current?.instance.refresh();
                },
                remove: (drawReceiptId: number) => {
                    return drawApi.deleteReceipt(drawReceiptId);
                },
                onRemoved: () => {
                    dataGridRef?.current?.instance.refresh();
                },
            }),
        [drawId]
    );

    const onSave = (data: IDrawReceipt) => {
        customStore.insert(data);
    };

    const editReceiptClick = (data: IDrawReceipt) => {
        set_activeReceipt(data);
        set_openAddEditReceiptsModal(true);
    };

    const downloadReceiptClick = async (data: IDrawReceipt) => {
        const response = await fileApi.downloadUrl({
            token: data.downloadToken,
        });

        if (response.statusCode !== 200) return;
        window.location.href = response.result.downloadUrl;
    };

    const deleteReceiptClick = (data: IDrawReceipt) => {
        if (data?.drawReceiptId) customStore.remove(data.drawReceiptId);
    };

    const actionButtons = [
        {
            id: 'closeManageReceiptsButton',
            theme: ButtonTheme.Primary,
            disabled: false,
            children: 'Close',
            width: '120px',
            handleClick: close,
        },
    ];

    return (
        <>
            {drawId && isOpen && (
                <>
                    <GenericPopup
                        id='drawReceiptsPopup'
                        isOpen={isOpen}
                        actionButtons={actionButtons}
                        handleClose={close}
                        aria-labelledby='alert-dialog-title'
                        hideOnOutsideClick={true}
                        showCloseButton={true}
                        showTitle={true}
                        title='Manage Receipts'
                        width='600px'
                        height='450px'
                        maxWidth='90%'
                        maxHeight='70%'
                        buttonAlignment='right'
                        wrapTitle={true}
                    >
                        <div>
                            <Button
                                id='openAddReceiptsButton'
                                dataTestid='openAddReceiptsButton'
                                theme={ButtonTheme.Tertiary}
                                handleClick={() => set_openAddEditReceiptsModal(true)}
                                width='120px'
                            >
                                <FontAwesomeIcon
                                    icon={faFileInvoiceDollar}
                                    size={'1x'}
                                    className='color-white'
                                    style={{ marginLeft: 3, marginRight: 8 }}
                                />
                                Add Receipt
                            </Button>
                            <DataGrid
                                ref={dataGridRef}
                                allowColumnResizing={true}
                                allowColumnReordering={true}
                                dataSource={customStore}
                                className='viewReceiptsModalV2'
                                showBorders={true}
                                showRowLines={true}
                                remoteOperations={false}
                                style={{ paddingTop: '12px' }}
                                height='250px'
                            >
                                <Column
                                    dataField='description'
                                    caption='Category'
                                />
                                <Column
                                    alignment='right'
                                    dataField='amount'
                                    format='currency'
                                />
                                <Column dataField='note' />
                                <Column
                                    cellRender={(params) => {
                                        return (
                                            <>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-around',
                                                    }}
                                                >
                                                    <IconButton
                                                        id='admin-icon-button'
                                                        size={IconButtonSize.Small}
                                                        theme={IconButtonTheme.Transparent}
                                                        icon={faEdit}
                                                        hint='Edit'
                                                        handleClick={() => editReceiptClick(params.data)}
                                                    />
                                                    <IconButton
                                                        id='admin-icon-button'
                                                        size={IconButtonSize.Small}
                                                        theme={IconButtonTheme.Transparent}
                                                        icon={faDownload}
                                                        hint='Download'
                                                        handleClick={() => downloadReceiptClick(params.data)}
                                                    />
                                                    <IconButton
                                                        id='admin-icon-button'
                                                        size={IconButtonSize.Small}
                                                        theme={IconButtonTheme.Transparent}
                                                        icon={faTrash}
                                                        hint='Delete'
                                                        handleClick={() => deleteReceiptClick(params.data)}
                                                    />
                                                </div>
                                            </>
                                        );
                                    }}
                                />
                                <Summary>
                                    <TotalItem
                                        column='drawReceiptId'
                                        summaryType='count'
                                        displayFormat='Total Count: {0}'
                                        showInColumn='Category'
                                    />
                                </Summary>
                            </DataGrid>
                        </div>
                    </GenericPopup>
                    <ReceiptsModalV2
                        isOpen={openAddEditReceiptsModal}
                        categories={categories}
                        onSave={onSave}
                        onClose={() => {
                            set_activeReceipt(undefined);
                            set_openAddEditReceiptsModal(false);
                        }}
                        receipt={activeReceipt}
                    />
                </>
            )}
        </>
    );
};

export default ViewReceiptsModalV2;
